import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { adminApi } from '../redux/adminApiSlice';
import LeafletMap from '../components/Map/LeafletMap';

function getApiBaseUrl(): string {
  const useRemoteApi = localStorage.getItem('useRemoteApi') === 'true';
  const remoteUrl = process.env.REACT_APP_REMOTE_API_URL || 'https://api.gridappsocial.com';
  const localUrl = process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3000';
  return useRemoteApi ? remoteUrl : localUrl;
}

const NAVBAR_HEIGHT = 64;

const DEFAULT_CENTER = { lat: 40.7535, lng: -74.0014 };
const RADIUS_MIN = 100;
const RADIUS_MAX = 5000;
const RADIUS_DEFAULT = 500;

const PulseSimulatorPage: React.FC = () => {
  const [durationMinutes, setDurationMinutes] = useState(20);
  const [actionsPerMinute, setActionsPerMinute] = useState(10);
  const [placeLimit, setPlaceLimit] = useState(30);
  const [useMapArea, setUseMapArea] = useState(true);
  const [center, setCenter] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER);
  const [radiusMeters, setRadiusMeters] = useState(RADIUS_DEFAULT);
  const [neighborhoodId, setNeighborhoodId] = useState<string>('');
  const [isRunning, setIsRunning] = useState(false);
  const [addCount, setAddCount] = useState(0);
  const [extendCount, setExtendCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [log, setLog] = useState<string[]>([]);
  const [clearError, setClearError] = useState<string | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const placesRef = useRef<Array<{ _id: string; name: string; type: string }>>([]);
  const placeIdToTypeRef = useRef<Map<string, string>>(new Map());
  const addCountRef = useRef(0);
  const extendCountRef = useRef(0);
  const errorCountRef = useRef(0);

  addCountRef.current = addCount;
  extendCountRef.current = extendCount;
  errorCountRef.current = errorCount;

  const { data: neighborhoods = [] } = adminApi.useGetPulseSimulatorNeighborhoodsQuery();
  const [fetchPlaces] = adminApi.useLazyGetPulseSimulatorPlacesQuery();
  const [fetchActivePulses] = adminApi.useLazyGetPulseSimulatorActivePulsesQuery();
  const [clearPulses, { isLoading: isClearing }] = adminApi.useClearPulseSimulatorPulsesMutation();

  const appendLog = useCallback((msg: string) => {
    setLog((prev) => [...prev.slice(-99), `[${new Date().toLocaleTimeString()}] ${msg}`]);
  }, []);

  const pick = useCallback(<T,>(arr: T[]): T => {
    return arr[Math.floor(Math.random() * arr.length)];
  }, []);

  const getPulseWordsForPlaceType = useCallback((placeType?: string): string[] => {
    const GLOBAL = ['Packed', 'Low-Key', 'Loud', 'Line', 'No Line', 'Busy'];
    const BY_TYPE: Record<string, string[]> = {
      bar: ['Dancing', 'High Energy', 'Rowdy', 'Flirty'],
      restaurant: ['Bustling', 'Cozy', 'Families', 'Romantic'],
      club: ['Sweaty', 'High Energy', 'Rowdy', 'Flirty'],
      gym: ['Open Floor', 'Dead', 'Intense', 'Social'],
      coffee_shop: ['Open Tables', 'Work Mode', 'Study Mode', 'Chatty'],
      event_space: ['Intimate', 'Chaotic', 'Engaged', 'Hyped'],
      park: ['Families', 'Games On', 'Laying Out', 'Social'],
    };
    if (!placeType) return GLOBAL;
    const key = placeType.toLowerCase().replace(/\s/g, '_');
    const extra = BY_TYPE[key];
    return extra ? [...GLOBAL, ...extra] : GLOBAL;
  }, []);

  const addPulseViaApi = useCallback(
    async (placeId: string, word: string): Promise<boolean> => {
      const baseUrl = getApiBaseUrl();
      try {
        const res = await fetch(`${baseUrl}/api/places/${placeId}/pulses`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ word }),
        });
        if (!res.ok) return false;
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  const extendPulseViaApi = useCallback(
    async (placeId: string, pulseId: string): Promise<boolean> => {
      const baseUrl = getApiBaseUrl();
      try {
        const res = await fetch(`${baseUrl}/api/places/${placeId}/pulses/${pulseId}/extend`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.status === 409 || res.status === 429 || res.status === 404) return false;
        if (!res.ok) return false;
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  const runTick = useCallback(async () => {
    const places = placesRef.current;
    if (places.length === 0) return;
    const placeIdToName = new Map(places.map((p) => [p._id, p.name]));
    const placeIdToType = placeIdToTypeRef.current;

    const doAdd = async () => {
      const place = pick(places);
      const placeId = place._id;
      const placeType = placeIdToType.get(placeId);
      const words = getPulseWordsForPlaceType(placeType);
      if (words.length === 0) return;
      const word = pick(words);
      const placeName = placeIdToName.get(placeId) ?? placeId;
      const ok = await addPulseViaApi(placeId, word);
      if (ok) {
        setAddCount((c) => c + 1);
        appendLog(`ADD  ${placeName}  "${word}"`);
      } else {
        setErrorCount((c) => c + 1);
      }
    };

    try {
      const placeIds = places.map((p) => p._id);
      if (placeIds.length === 0) {
        await doAdd();
        return;
      }
      const result = await fetchActivePulses(placeIds);
      const pulses = result.data?.pulses ?? [];
      const canExtend = pulses.length > 0;

      if (canExtend && Math.random() < 0.5) {
        const pulse = pick(pulses);
        const placeId = String(pulse.place);
        const pulseId = pulse._id;
        const placeName = placeIdToName.get(placeId) ?? placeId;
        const ok = await extendPulseViaApi(placeId, pulseId);
        if (ok) {
          setExtendCount((c) => c + 1);
          appendLog(`EXTEND  ${placeName}  "${pulse.word}"`);
        } else {
          await doAdd();
        }
      } else {
        await doAdd();
      }
    } catch {
      await doAdd();
    }
  }, [addPulseViaApi, extendPulseViaApi, getPulseWordsForPlaceType, pick, appendLog, fetchActivePulses]);

  const handleStart = useCallback(async () => {
    setAddCount(0);
    setExtendCount(0);
    setErrorCount(0);
    setLog([]);

    const nid = neighborhoodId;
    const nName = nid ? (neighborhoods as Array<{ _id: string; name: string }>).find((n) => n._id === nid)?.name ?? '' : '';

    try {
      const params = useMapArea
        ? { latitude: center.lat, longitude: center.lng, radiusMeters, limit: placeLimit }
        : { neighborhoodName: nName, limit: placeLimit };
      const result = await fetchPlaces(params);
      const places = result.data?.places ?? [];
      if (places.length === 0) {
        appendLog('No places found. Check neighborhood filter and place limit.');
        return;
      }
      placesRef.current = places;
      placeIdToTypeRef.current = new Map(places.map((p) => [p._id, p.type]));
    } catch (err) {
      appendLog('Failed to fetch places: ' + String(err));
      return;
    }

    setIsRunning(true);
    startTimeRef.current = Date.now();
    appendLog(`Started. ${placesRef.current.length} places, ${actionsPerMinute} actions/min, ${durationMinutes} min.`);
  }, [neighborhoodId, neighborhoods, placeLimit, useMapArea, center, radiusMeters, fetchPlaces, durationMinutes, actionsPerMinute, appendLog]);

  const handleStop = useCallback(() => {
    setIsRunning(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const elapsed = ((Date.now() - startTimeRef.current) / 1000 / 60).toFixed(1);
    appendLog(
      `Stopped. Adds: ${addCountRef.current}, Extends: ${extendCountRef.current}, Errors: ${errorCountRef.current} (${elapsed} min)`
    );
  }, [appendLog]);

  useEffect(() => {
    if (!isRunning) return;
    const totalMs = durationMinutes * 60 * 1000;
    const intervalMs = Math.floor(60_000 / actionsPerMinute);
    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed >= totalMs) {
        handleStop();
        return;
      }
      const jitter = 0.5 + Math.random();
      const delay = Math.floor(intervalMs * jitter);
      timeoutRef.current = setTimeout(async () => {
        if (cancelled) return;
        const elapsedNow = Date.now() - startTimeRef.current;
        if (elapsedNow >= totalMs) {
          handleStop();
          return;
        }
        await runTick();
        scheduleNext();
      }, delay);
    };

    scheduleNext();
    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isRunning, durationMinutes, actionsPerMinute, runTick, handleStop]);

  const handleClearAll = useCallback(async () => {
    setClearError(null);
    try {
      const result = await clearPulses().unwrap();
      appendLog(`Cleared all pulses. Deleted: ${result.deletedCount}`);
    } catch (err) {
      setClearError(String(err));
    }
  }, [clearPulses, appendLog]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: NAVBAR_HEIGHT,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: '#f5f5f5',
        overflow: 'auto',
        p: 2,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        Pulse Simulator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Simulates pulse add/extend activity. Uses the live API so socket events fire and the app sees updates. Keep this tab open.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={useMapArea}
                onChange={(e) => setUseMapArea(e.target.checked)}
                disabled={isRunning}
              />
            }
            label="Filter by map area (circle)"
            sx={{ mb: 2, display: 'block' }}
          />

          {useMapArea && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Map – drag marker to move center, adjust radius to expand/contract circle
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 300, height: 350 }}>
                  <LeafletMap
                    center={center}
                    zoom={15}
                    height={350}
                    searchRadiusMeters={radiusMeters}
                    onLocationChange={(loc) => setCenter(loc)}
                    draggable={!isRunning}
                  />
                </Box>
                <Box sx={{ minWidth: 200 }}>
                  <TextField
                    label="Latitude"
                    type="number"
                    value={center.lat}
                    onChange={(e) =>
                      setCenter((c) => ({ ...c, lat: parseFloat(e.target.value) || c.lat }))
                    }
                    disabled={isRunning}
                    size="small"
                    fullWidth
                    sx={{ mb: 1 }}
                    inputProps={{ step: 0.0001 }}
                  />
                  <TextField
                    label="Longitude"
                    type="number"
                    value={center.lng}
                    onChange={(e) =>
                      setCenter((c) => ({ ...c, lng: parseFloat(e.target.value) || c.lng }))
                    }
                    disabled={isRunning}
                    size="small"
                    fullWidth
                    sx={{ mb: 1 }}
                    inputProps={{ step: 0.0001 }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                    Radius: {radiusMeters} m
                  </Typography>
                  <Slider
                    value={radiusMeters}
                    onChange={(_, v) => setRadiusMeters(v as number)}
                    min={RADIUS_MIN}
                    max={RADIUS_MAX}
                    step={50}
                    disabled={isRunning}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v} m`}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {!useMapArea && (
            <FormControl size="small" sx={{ minWidth: 200, mb: 2 }} disabled={isRunning}>
              <InputLabel>Neighborhood</InputLabel>
              <Select
                value={neighborhoodId}
                label="Neighborhood"
                onChange={(e) => setNeighborhoodId(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {(neighborhoods as Array<{ _id: string; name: string }>).map((n) => (
                  <MenuItem key={n._id} value={n._id}>
                    {n.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              label="Duration (min)"
              type="number"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(Math.max(1, parseInt(e.target.value, 10) || 1))}
              disabled={isRunning}
              size="small"
              sx={{ width: 100 }}
            />
            <TextField
              label="Actions/min"
              type="number"
              value={actionsPerMinute}
              onChange={(e) =>
                setActionsPerMinute(Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 1)))
              }
              disabled={isRunning}
              size="small"
              sx={{ width: 100 }}
            />
            <TextField
              label="Place limit"
              type="number"
              value={placeLimit}
              onChange={(e) => setPlaceLimit(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 30)))}
              disabled={isRunning}
              size="small"
              sx={{ width: 100 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrowIcon />}
              onClick={handleStart}
              disabled={isRunning}
            >
              Start
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={handleStop}
              disabled={!isRunning}
            >
              Stop
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DeleteSweepIcon />}
              onClick={handleClearAll}
              disabled={isRunning || isClearing}
            >
              Clear all pulses
            </Button>
          </Box>
          {isRunning && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress sx={{ height: 6, borderRadius: 1 }} />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            Adds: {addCount} | Extends: {extendCount} | Errors: {errorCount}
          </Typography>
          {clearError && (
            <Alert severity="error" onClose={() => setClearError(null)} sx={{ mt: 1 }}>
              {clearError}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
        <Typography variant="subtitle2" gutterBottom>
          Log
        </Typography>
        <Box
          component="pre"
          sx={{
            fontFamily: 'monospace',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            m: 0,
          }}
        >
          {log.length === 0 ? 'No activity yet.' : log.join('\n')}
        </Box>
      </Paper>
    </Box>
  );
};

export default PulseSimulatorPage;
